import { StyleSheet, View, TouchableOpacity, Platform, Image } from "react-native";
import React, { useState, useEffect } from "react";
import { Ionicons } from "@expo/vector-icons";
import { GroupModel, ShareModel, Contact, ContactModel, ICustomCardEntityConfig, formatCardTimestamp } from "common247";
import { ThemedText } from "../ThemedText";
import { ShareSelector } from "../sharing/ShareSelector";
import { useThemeColor } from "../../hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { ThemedListViewMode } from "../ThemedList";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedChip } from "../ThemedChip";
import { ThemedRating } from "../ThemedRating";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import {
  resolveCardLinkedContacts,
  resolveCardLinkedGroups,
  resolveCardLinkedMedia,
  resolveCardLinkedMemories,
  resolveCardLinkedPlaces,
  resolveCardLinkedAnchors,
} from "../cards/card-helpers";
import TagListStripDefault, { TagListStrip as TagListStripNamed } from "@/components/tags/TagListStrip";
const TagListStrip = TagListStripNamed || TagListStripDefault;

/**
 * Props for the GroupCard component.
 */
export interface IGroupCardProps {
  group: GroupModel;
  allGroups?: GroupModel[];
  isAdminView?: boolean;
  /** Whether the user is allowed to add or remove tags (explicit override) */
  canEditTags?: boolean;
  currentUserId?: string;
  compact?: boolean;
  viewMode?: ThemedListViewMode;
  /** Whether this card is currently hovered in the parent list */
  isHovered?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onPress?: () => void;
  onLongPress?: () => void;
  onContextMenu?: (e?: any) => void;
  onEditPress?: () => void;
  onDelete?: () => void;
  onInspect?: () => void;

  /** Existing shares associated with this group */
  shares?: ShareModel[];
  /** Available contacts to select from when adding a new share */
  availableContacts?: (Contact | ContactModel)[];
  /** Available groups to select from when adding a new share */
  availableGroups?: GroupModel[];
  /** Available media to resolve titles and thumbnails */
  availableMedia?: any[];
  /** Available places to resolve linked place metadata */
  availablePlaces?: any[];
  /** Available anchors to resolve linked anchor metadata */
  availableAnchors?: any[];
  /** Loading flag while fetching shares */
  isLoadingShares?: boolean;
  /** Callback when a new share is created or role updated */
  onShareTarget?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  /** Callback when a share is revoked */
  onUnshare?: (shareId: string) => void;
  /** Controlled expanded state for sharing control */
  isSharingExpanded?: boolean;
  /** Callback to toggle expanded sharing control */
  onToggleSharing?: () => void;
  onShare?: () => void;
  /** Optional custom card configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * Card component displaying group details, role, actions, and shares.
 */
export function GroupCard({
  group,
  allGroups = [],
  isAdminView = false,
  canEditTags,
  currentUserId,
  compact = false,
  viewMode,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onPress,
  onLongPress,
  onContextMenu,
  onEditPress,
  onDelete,
  onInspect,
  onShare,
  shares,
  availableContacts,
  availableGroups,
  availableMedia,
  availablePlaces,
  availableAnchors,
  isLoadingShares,
  onShareTarget,
  onUnshare,
  isSharingExpanded,
  onToggleSharing,
  config,
}: IGroupCardProps) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isSharingExpanded !== undefined ? isSharingExpanded : localExpanded;

  const [lastKnownShares, setLastKnownShares] = useState<ShareModel[] | null>(null);

  useEffect(() => {
    if (shares !== undefined) {
      setLastKnownShares(shares);
    }
  }, [shares]);

  const userRole = isAdminView ? "viewer" : (group.userRole || (currentUserId === group.ownerId ? "owner" : "viewer"));
  const isOwner = isAdminView ? false : (userRole === "owner");
  const isViewer = isAdminView ? true : (userRole === "viewer");
  const isEditor = isAdminView ? false : (userRole === "editor");
  const isAllContacts = group.groupType === "allcontacts";
  const canEdit = !isAdminView && (isOwner || isEditor) && !isAllContacts;
  const canDelete = !isAdminView && isOwner && !isAllContacts;
  const effectiveCanEdit = !isAdminView && canEdit;
  const effectiveCanEditTags = canEditTags !== undefined ? canEditTags : (!isAdminView && canEdit);
  const effectiveIsOwnerForTags = (canEditTags === false || isAdminView) ? false : isOwner;

  const roleName = isOwner ? undefined : isViewer ? "Shared Group (View Only)" : "Shared Group";

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const tintColor = useThemeColor({}, "tint");
  const activeBorderColor = isSelected ? tintColor : isHovered ? hoverBorderColor : borderColor;
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({ light: "#64748B", dark: "#94A3B8" }, "text");

  const handleSharePress = (e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (onToggleSharing) {
      onToggleSharing();
    } else if (onShareTarget || shares !== undefined) {
      setLocalExpanded(!localExpanded);
    } else if (onShare) {
      onShare();
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  const effectiveShares: ShareModel[] =
    shares ??
    lastKnownShares ??
    (group.sharedWith?.map((s) => ({
      id: s.id,
      sourceId: group.id || "",
      sourceType: "group" as const,
      targetId: s.id,
      targetType: s.type,
      role: s.role,
      targetDisplayName: s.displayName,
      targetUserId: s.targetUserId,
      sharedBy: group.ownerId || "",
      sharedAt: group.createdAt,
    })) as ShareModel[]) ??
    [];

  const canShowShareButton = group.canShare && (onShare || onShareTarget || onToggleSharing || shares !== undefined);

  const kebabActions: IKebabActionItem[] = [];
  if (canShowShareButton) {
    kebabActions.push({
      id: "share",
      label: isExpanded ? "Close Sharing" : "Manage Sharing",
      icon: "share-social-outline",
      onPress: handleSharePress,
    });
  }
  if (canEdit && onEditPress) {
    kebabActions.push({
      id: "edit",
      label: "Edit Group",
      icon: "pencil-outline",
      onPress: onEditPress,
    });
  }
  if (canDelete && onDelete) {
    kebabActions.push({
      id: "delete",
      label: "Delete Group",
      icon: "trash-outline",
      destructive: true,
      onPress: onDelete,
    });
  }

  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="group"
          id={group.id}
          label={group.name || "Group"}
          isOwner={isOwner}
          onPress={onPress}
          onLongPress={onLongPress}
        />
      </View>
    );
  }

  const resolvedMemories = resolveCardLinkedMemories(group);
  const resolvedMedia = resolveCardLinkedMedia(group, availableMedia);
  const resolvedContacts = resolveCardLinkedContacts(group, availableContacts);
  const resolvedGroups = resolveCardLinkedGroups(group, allGroups);
  const resolvedPlaces = resolveCardLinkedPlaces(group, availablePlaces);
  const resolvedAnchors = resolveCardLinkedAnchors(group, availableAnchors);

  if (viewMode === "medium") {
    return (
      <ThemedMediumCard
        entityType="group"
        id={group.id}
        title={group.name || "Group"}
        description={group.description}
        thumbnailUrl={group.photoIconUrl || group.photoUrl}
        ownerDisplayName={!isOwner ? (group.ownerDisplayName || "Unknown") : undefined}
        isOwner={isOwner}
        isFiveStar={group.favouriteRating === 5}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={group.tags}
        sharesList={effectiveShares}
        createdAt={group.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "full") {
    return (
      <ThemedFullCard
        entityType="group"
        id={group.id}
        title={group.name || "Group"}
        description={group.description}
        thumbnailUrl={group.photoIconUrl || group.photoUrl}
        ownerDisplayName={!isOwner ? (group.ownerDisplayName || "Unknown") : undefined}
        isOwner={isOwner}
        isFiveStar={group.favouriteRating === 5}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        anchorsList={resolvedAnchors}
        tagsList={group.tags}
        sharesList={effectiveShares}
        createdAt={group.createdAt}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="group"
        id={group.id}
        item={group}
        title={group.name || "Group"}
        description={group.description}
        isOwner={isOwner}
        kebabActions={kebabActions}
        mediaList={resolvedMedia}
        contactsList={resolvedContacts}
        groupsList={resolvedGroups}
        memoriesList={resolvedMemories}
        placesList={resolvedPlaces}
        tagsList={group.tags}
        sharesList={effectiveShares}
        config={config}
        onPress={onPress}
        onEditPress={onEditPress}
        onDeletePress={onDelete}
        onSharePress={onShare || handleSharePress}
      />
    );
  }

  if (viewMode === "details") {
    return (
      <View
        style={[
          styles.detailsRowContainer,
          { backgroundColor: cardBgColor, borderColor: activeBorderColor },
        ]}
      >
        <TouchableOpacity
          style={styles.detailsRowMain}
          onPress={onPress}
          onLongPress={onLongPress}
          delayLongPress={350}
          activeOpacity={0.7}
          {...(Platform.OS === "web" && onContextMenu ? {
            onContextMenu: (e: any) => {
              e.preventDefault();
              onContextMenu(e);
            },
          } : {})}
        >
          {selectionMode || isSelected ? (
            <View style={[styles.detailsCheckbox, isSelected && { backgroundColor: tintColor, borderColor: tintColor }]}>
              {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
            </View>
          ) : null}
          <View style={[styles.detailsIconContainer, { backgroundColor: badgeBg }]}>
            {group.photoIconUrl || group.photoUrl ? (
              <Image
                source={{ uri: group.photoIconUrl || group.photoUrl }}
                style={styles.detailsAvatarImage}
                resizeMode="cover"
              />
            ) : (
              <ThemedText style={{ fontSize: 14 }}>👥</ThemedText>
            )}
          </View>

          <View style={styles.detailsInfo}>
            <View style={styles.detailsNameRow}>
              {isOwner ? (
                <ThemedPlatformSymbol type="owner" size="small" />
              ) : (
                <ThemedPlatformSymbol type="shared" size="small" />
              )}
              <ThemedText type="defaultSemiBold" style={styles.detailsGroupName} numberOfLines={1}>
                {group.name}
              </ThemedText>
              {!isOwner && roleName ? (
                <View style={[styles.inlineRoleBadge, { backgroundColor: badgeBg }]}>
                  <ThemedText style={styles.gridRoleBadgeText}>{roleName}</ThemedText>
                </View>
              ) : null}
              {group.groupType === "allcontacts" && (
                <View style={[styles.inlineRoleBadge, { backgroundColor: "rgba(255, 91, 3, 0.1)" }]}>
                  <ThemedText style={[styles.gridRoleBadgeText, { color: tintColor }]}>All Contacts</ThemedText>
                </View>
              )}
            </View>

            <View style={styles.detailsSubRow}>
              <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                {group.description || "No description"}
              </ThemedText>
              {group.childGroupIds && group.childGroupIds.length > 0 ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • {group.childGroupIds.length} {group.childGroupIds.length === 1 ? "child group" : "child groups"}
                </ThemedText>
              ) : null}
              {group.sharedTo && group.sharedTo.length > 0 ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • 📖 {group.sharedTo.length} {group.sharedTo.length === 1 ? "item" : "items"}
                </ThemedText>
              ) : null}
              {!isOwner && group.ownerDisplayName ? (
                <ThemedText style={styles.detailsMetaText} numberOfLines={1}>
                  • Shared by: {group.ownerDisplayName}
                </ThemedText>
              ) : null}
            </View>
            <TagListStrip
              entityId={group.id!}
              entityType="group"
              tags={group.tags}
              canEdit={effectiveCanEditTags}
              isOwner={effectiveIsOwnerForTags}
              currentUserId={currentUserId}
              compact={true}
            />
            {effectiveShares.length > 0 && (
              <TouchableOpacity
                onPress={handleSharePress}
                style={{ marginTop: 2, alignSelf: "flex-start" }}
                activeOpacity={0.7}
                testID={`group-share-pill-details-${group.id || "card"}`}
              >
                <ThemedText style={[styles.detailsMetaText, { color: tintColor, fontWeight: "600" }]} numberOfLines={1}>
                  👥 Shared ({effectiveShares.length})
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>

          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={true}
              accessibilityLabel={`Options for ${group.name}`}
            />
          )}
        </TouchableOpacity>

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={group.name}
          itemType="group"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={group.canShare}
          canUnshare={group.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`group-share-picker-details-${group.id || "card"}`}
        />
      </View>
    );
  }

  if (viewMode === "compact") {
    return (
      <View style={{ position: "relative", width: "100%" }}>
        <ThemedCompactCard
          entityType="group"
          id={group.id}
          title={group.name || "Group"}
          thumbnailUrl={group.photoIconUrl || group.photoUrl}
          isOwner={isOwner}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onSelect={onPress}
          onPress={onPress}
          onLongPress={onLongPress}
          onContextMenu={onContextMenu}
          kebabActions={kebabActions}
          testID={`group-compact-card-${group.id || "card"}`}
        />

        <ShareSelector
          visible={isExpanded}
          onClose={handleSharePress}
          itemTitle={group.name}
          itemType="group"
          shares={effectiveShares}
          contacts={availableContacts || []}
          groups={availableGroups || []}
          isLoading={isLoadingShares}
          canShare={group.canShare}
          canUnshare={group.canShare}
          onShare={onShareTarget || (() => {})}
          onUnshare={onUnshare || (() => {})}
          testID={`group-share-picker-compact-${group.id || "card"}`}
        />
      </View>
    );
  }

  const isSmall = viewMode === "small" || compact;

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isSmall && styles.cardCompact,
        { backgroundColor: cardBgColor, borderColor: activeBorderColor },
      ]}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={350}
      activeOpacity={0.7}
      {...(Platform.OS === "web" && onContextMenu ? {
        onContextMenu: (e: any) => {
          e.preventDefault();
          onContextMenu(e);
        },
      } : {})}
    >
      <View style={styles.cardHeader}>
        {selectionMode && (
          <TouchableOpacity
            style={[
              styles.detailsCheckbox,
              isSelected && { backgroundColor: tintColor, borderColor: tintColor },
            ]}
            onPress={(e) => {
              e.stopPropagation?.();
              onPress?.();
            }}
          >
            {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </TouchableOpacity>
        )}
        <View style={styles.cardHeaderAvatarContainer}>
          {group.photoIconUrl || group.photoUrl ? (
            <Image
              source={{ uri: group.photoIconUrl || group.photoUrl }}
              style={styles.cardHeaderAvatar}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: badgeBg }]}>
              <Ionicons name="people-outline" size={18} color={tintColor} />
            </View>
          )}
        </View>

        <View style={styles.titleArea}>
          <View style={styles.titleRow}>
            <ThemedText type="defaultSemiBold" style={[styles.cardTitle, compact && styles.cardTitleCompact]}>
              {group.name}
            </ThemedText>
            {group.favouriteRating ? (
              <View style={{ marginLeft: 6 }}>
                <ThemedRating
                  value={group.favouriteRating}
                  size={12}
                  showLabel={false}
                  readOnly={true}
                />
              </View>
            ) : null}
          </View>

          <View style={styles.roleBadgeRow}>
            {!isOwner && roleName ? (
              <View style={[styles.roleBadge, { backgroundColor: badgeBg }]}>
                <ThemedText type="stamp" style={styles.roleBadgeText}>{roleName}</ThemedText>
              </View>
            ) : null}
            {!isOwner && <ThemedText style={[styles.ownerText, { color: tintColor }]}>• Shared by: {group.ownerDisplayName || "Unknown"}</ThemedText>}
            {group.groupType === "allcontacts" && (
              <View style={styles.typeBadge}>
                <ThemedText type="stamp" style={styles.typeBadgeText}>All Contacts</ThemedText>
              </View>
            )}
          </View>
          {isAdminView && <ThemedText style={styles.groupId}>ID: {group.id}</ThemedText>}
        </View>

        <View style={styles.cardTopRightGroup}>
          <View style={styles.symbolsGroup}>
            {isOwner ? (
              <ThemedPlatformSymbol type="owner" size="small" />
            ) : (
              <ThemedPlatformSymbol type="shared" size="small" />
            )}
          </View>
          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={compact}
              accessibilityLabel={`Options for ${group.name}`}
            />
          )}
        </View>
      </View>

      <ThemedText style={styles.cardDesc}>{group.description || "No description."}</ThemedText>

      {/* Contact & Groups Section (Single Section Standard for Members & Child Groups) */}
      {(() => {
        const memberCount = (group.contactIds?.length || 0);
        const childGroupCount = (group.childGroupIds?.length || 0);
        const totalCount = memberCount + childGroupCount;
        if (totalCount === 0) return null;

        return (
          <View style={styles.contactAndGroupsSection}>
            <View style={styles.contactAndGroupsHeaderRow}>
              <Ionicons name="people-outline" size={11} color={tintColor} />
              <ThemedText type="stamp" style={[styles.contactAndGroupsStamp, { color: textMuted }]}>
                CONTACT &amp; GROUPS ({totalCount})
              </ThemedText>
            </View>
            <View style={styles.chipRow}>
              {group.childGroupIds?.map((id) => {
                const g = allGroups.find((item) => item.id === id);
                return (
                  <ThemedChip
                    key={`child-group-${id}`}
                    entityType="group"
                    id={id}
                    label={g ? g.name : "Group"}
                    compact={isSmall}
                  />
                );
              })}
              {group.contactIds?.map((cId) => {
                const c = availableContacts?.find((item: any) => item.id === cId);
                return (
                  <ThemedChip
                    key={`member-contact-${cId}`}
                    entityType="contact"
                    id={cId}
                    label={(c as any)?.displayName || (c as any)?.name || "Member"}
                    compact={isSmall}
                  />
                );
              })}
            </View>
          </View>
        );
      })()}

      {group.sharedTo && group.sharedTo.length > 0 && (
        <View style={styles.memoryInfo}>
          <ThemedText style={styles.memoriesLabel}>Used In</ThemedText>
          <ThemedText style={styles.memoryInfoText} numberOfLines={1}>
            📖 {group.sharedTo.length} {group.sharedTo.length === 1 ? "item" : "items"}
          </ThemedText>
        </View>
      )}

      {/* Shares Section (Proposed 5-Tier Item Card Standard) */}
      {effectiveShares.length > 0 && (
        <View style={[styles.traySection, { borderTopColor: borderColor }]}>
          <View style={styles.trayHeaderRow}>
            <Ionicons name="share-social-outline" size={11} color={tintColor} />
            <ThemedText type="stamp" style={[styles.trayTitle, { color: textMuted }]}>
              SHARED WITH ({effectiveShares.length})
            </ThemedText>
          </View>
          <View style={styles.chipsWrap}>
            {effectiveShares.map((s, idx) => (
              <ThemedChip
                key={`group-share-${s.id || idx}`}
                entityType="share"
                id={s.id}
                label={group.name || "Group"}
                secondaryLabel={s.targetDisplayName || s.targetId || "Recipient"}
                compact={isSmall}
                onPress={handleSharePress}
              />
            ))}
          </View>
          <TouchableOpacity
            style={[styles.compactSharePill, { display: "none" }]}
            onPress={group.canShare ? handleSharePress : undefined}
            activeOpacity={group.canShare ? 0.7 : 1}
            accessibilityLabel={`Shared with ${effectiveShares.length} recipients. Tap to manage sharing.`}
            testID={`group-share-pill-${group.id || "card"}`}
          >
            <ThemedText style={[styles.compactShareText, { color: tintColor }]}>
              Shared ({effectiveShares.length})
            </ThemedText>
          </TouchableOpacity>
        </View>
      )}

      {isAdminView && (
        <View style={[styles.detailsRow, { backgroundColor: badgeBg }]}>
          <View style={styles.detailItem}>
            <ThemedText style={styles.detailLabel}>Owner</ThemedText>
            <ThemedText style={styles.detailValue}>
              {group.ownerDisplayName || "Unknown"} ({group.ownerId})
            </ThemedText>
          </View>
          <View style={styles.detailItem}>
            <ThemedText style={styles.detailLabel}>Members</ThemedText>
            <ThemedText style={styles.detailValue}>{group.contactIds?.length || 0}</ThemedText>
          </View>
        </View>
      )}

      <TagListStrip
        entityId={group.id!}
        entityType="group"
        tags={group.tags}
        canEdit={effectiveCanEditTags}
        isOwner={effectiveIsOwnerForTags}
        currentUserId={currentUserId}
        compact={compact}
      />

      <View style={[styles.cardFooter, { borderTopColor: borderColor }]}>
        <View style={styles.footerLeft}>
          <ThemedText style={styles.dateText}>Created: {formatCardTimestamp(group.createdAt)}</ThemedText>
        </View>
        {isAdminView && onInspect && (
          <TouchableOpacity style={styles.manageBtn} onPress={onInspect}>
            <ThemedText style={styles.manageText}>Inspect</ThemedText>
          </TouchableOpacity>
        )}
      </View>

      <ShareSelector
        visible={isExpanded}
        onClose={handleSharePress}
        itemTitle={group.name}
        itemType="group"
        shares={effectiveShares}
        contacts={availableContacts || []}
        groups={availableGroups || []}
        isLoading={isLoadingShares}
        canShare={group.canShare}
        canUnshare={group.canShare}
        onShare={onShareTarget || (() => {})}
        onUnshare={onUnshare || (() => {})}
        testID={`group-share-picker-${group.id || "card"}`}
      />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    marginBottom: 6,
    gap: 8,
  },
  cardCompact: {
    padding: 6,
    borderRadius: 4,
    marginBottom: 4,
  },
  cardTopRightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    zIndex: 10,
  },
  symbolsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 2,
  },
  cardHeaderAvatarContainer: {
    width: 38,
    height: 38,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 1,
    flexShrink: 0,
  },
  cardHeaderAvatar: {
    width: 38,
    height: 38,
    borderRadius: 4,
  },
  avatarPlaceholder: {
    width: 38,
    height: 38,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  detailsAvatarImage: {
    width: "100%",
    height: "100%",
    borderRadius: 4,
  },
  traySection: {
    borderTopWidth: 1,
    paddingTop: 6,
    gap: 4,
    marginTop: 4,
  },
  trayHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  trayTitle: {
    fontSize: 9,
    letterSpacing: 0.8,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 2,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
    flex: 1,
  },
  cardTitleCompact: {
    fontSize: 11,
    fontWeight: "600",
  },
  typeBadge: {
    backgroundColor: "rgba(255, 91, 3, 0.1)",
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(255, 91, 3, 0.3)",
  },
  typeBadgeText: {
    fontSize: 8.5,
    color: "#FF5B03",
    fontWeight: "bold",
  },
  groupId: {
    fontSize: 8.5,
    opacity: 0.4,
    fontFamily: "monospace",
  },
  cardDesc: {
    fontSize: 10.5,
    marginBottom: 3,
    opacity: 0.8,
  },
  childGroupsInfo: {
    marginBottom: 3,
  },
  childGroupsLabel: {
    fontSize: 9.5,
    fontWeight: "bold",
    opacity: 0.7,
  },
  childGroupsList: {
    fontSize: 9.5,
    opacity: 0.6,
  },
  detailsRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 4,
    padding: 4,
    backgroundColor: "rgba(0,0,0,0.02)",
    borderRadius: 0,
  },
  detailItem: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 8.5,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.5,
    fontWeight: "bold",
    marginBottom: 1,
  },
  detailValue: {
    fontSize: 9.5,
  },
  cardFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
    paddingTop: 4,
  },
  footerLeft: {
    gap: 2,
  },
  dateText: {
    fontSize: 9.5,
    opacity: 0.5,
  },
  roleBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 3,
  },
  roleBadge: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  viewerBadge: {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  editorBadge: {
    backgroundColor: "rgba(74, 144, 226, 0.1)",
  },
  roleBadgeText: {
    fontSize: 8.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    opacity: 0.6,
  },
  gridActionRow: {
    flexDirection: "row",
    gap: 2,
  },
  gridActionBtn: {
    padding: 2,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  gridDeleteBtn: {
    backgroundColor: "rgba(217, 48, 37, 0.1)",
  },
  gridActionIcon: {
    fontSize: 9,
    opacity: 0.6,
  },
  contactAndGroupsSection: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  contactAndGroupsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 4,
  },
  contactAndGroupsStamp: {
    fontSize: 9,
    letterSpacing: 0.5,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  actionRow: {
    flexDirection: "row",
    gap: 4,
  },
  shareBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(74, 144, 226, 0.1)",
    borderRadius: 0,
  },
  shareText: {
    fontSize: 10,
    color: "#4A90E2",
    fontWeight: "bold",
  },
  actionBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 0,
  },
  actionText: {
    fontSize: 10,
    fontWeight: "bold",
    opacity: 0.7,
  },
  actionBtnDelete: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "#ffcccc",
    borderRadius: 0,
  },
  actionTextDelete: {
    fontSize: 10,
    color: "#cc0000",
    fontWeight: "bold",
  },
  manageBtn: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
    backgroundColor: "rgba(74, 144, 226, 0.1)",
    borderRadius: 0,
  },
  manageText: {
    fontSize: 10,
    color: "#4A90E2",
    fontWeight: "bold",
  },
  sharePillContainer: {
    marginTop: 3,
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  compactSharePill: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: ButtonPaddings.compact.paddingVertical,
    paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
    borderWidth: 1,
    borderRadius: 0,
    alignSelf: "flex-start",
  },
  compactShareText: {
    fontSize: 11,
    fontWeight: "600",
  },
  sharesContainer: {
    marginTop: 2,
    marginBottom: 4,
  },
  sharesHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 3,
  },
  manageSharesText: {
    fontSize: 9.5,
    fontWeight: "600",
  },
  inlineSharingSection: {
    marginTop: 4,
    marginBottom: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  inlineSharingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  inlineSharingTitle: {
    fontSize: 11,
    letterSpacing: -0.2,
  },
  collapseBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 0,
  },
  collapseBtnText: {
    fontSize: 9.5,
    fontWeight: "600",
    opacity: 0.7,
  },
  sharesLabel: {
    fontSize: 9,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
  },
  sharesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
  },
  memoryInfo: {
    marginTop: 2,
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  memoriesLabel: {
    fontSize: 9,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
  },
  memoryInfoText: {
    fontSize: 10,
    opacity: 0.7,
  },
  ownerText: {
    fontSize: 9.5,
    opacity: 0.6,
    fontStyle: "italic",
  },
  // Details view styles
  detailsRowContainer: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    marginVertical: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsRowMain: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 6,
    gap: 6,
  },
  detailsIconContainer: {
    width: 28,
    height: 28,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  detailsInfo: {
    flex: 1,
    justifyContent: "center",
    gap: 1,
  },
  detailsNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  detailsGroupName: {
    fontSize: 11,
    fontWeight: "600",
    flexShrink: 1,
  },
  detailsSubRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  },
  detailsMetaText: {
    fontSize: 9.5,
    opacity: 0.65,
  },
  inlineRoleBadge: {
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderRadius: 0,
  },
  gridRoleBadgeText: {
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.8,
  },
  // Compact view styles (14 columns)
  compactModeCard: {
    position: "relative",
    width: "100%",
    alignSelf: "stretch",
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderRadius: 0,
    borderWidth: 1,
    minHeight: 38,
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  compactModeTitle: {
    fontSize: 9.5,
    fontWeight: "600",
    lineHeight: 12,
  },
  detailsCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 0,
    borderWidth: 1.5,
    borderColor: "rgba(150, 150, 150, 0.4)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  smallSelectionBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 14,
    height: 14,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  selectionBadge: {
    position: "absolute",
    top: 6,
    right: 34,
    width: 20,
    height: 20,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 14,
  },
  selectionText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  cardTopRightAction: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 15,
  },
  compactTopRightAction: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 15,
  },
});
